import { ChevronLeft, ShieldCheck, MailOpen } from 'lucide-react';
import { useState, useEffect } from 'react';
import { StatusDisplay } from './StatusDisplay';
import { useInterval } from '~/lib/hooks/useInterval';

interface EmailOnboardingProps {
  email: string;
  handleBackToLogin: () => void;
  isInitialSending?: boolean;
  setSuccess: (success: boolean) => void;
  success: boolean;
}

export default function EmailOnboarding({
  email,
  handleBackToLogin,
  isInitialSending = false,
  setSuccess,
  success,
}: EmailOnboardingProps) {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(30);

  // Countdown timer
  useInterval(
    () => {
      if (countdown > 0) {
        setCountdown(countdown - 1);
      } else {
        setCanResend(true);
      }
    },
    !canResend ? 1000 : null, // Stop interval when canResend is true
  );

  const handleSendOTP = async () => {
    console.log('[EmailOnboarding] === Starting OTP Send Process ===');
    console.log('[EmailOnboarding] Email:', email);

    setIsLoading(true);
    setError('');

    try {
      console.log('[EmailOnboarding] Sending OTP request to API...');

      const response = await fetch('/api/otp/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('[EmailOnboarding] OTP send response status:', response.status);

      const data = (await response.json()) as { error?: string; message?: string };
      console.log('[EmailOnboarding] OTP send response data:', data);

      if (!response.ok) {
        console.error('[EmailOnboarding] OTP send failed:', data.error);
        setError(data.error || 'Failed to send OTP');
        return;
      }

      console.log('[EmailOnboarding] OTP sent successfully');
      setSuccess(true);
    } catch (error) {
      console.error('[EmailOnboarding] === Error in OTP Send Process ===');
      console.error('[EmailOnboarding] Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('[EmailOnboarding] Error message:', error instanceof Error ? error.message : error);
      console.error('[EmailOnboarding] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    console.log('[EmailOnboarding] === Starting OTP Verify Process ===');
    console.log('[EmailOnboarding] Email:', email);
    console.log('[EmailOnboarding] OTP:', otp);
    setSuccess(false);

    if (!otp || otp.length !== 6) {
      console.error('[EmailOnboarding] Invalid OTP length:', otp.length);
      setError('Please enter a 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      console.log('[EmailOnboarding] Sending OTP verification request to API...');

      const response = await fetch('/api/otp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      console.log('[EmailOnboarding] OTP verify response status:', response.status);

      const data = (await response.json()) as { error?: string; user?: any; token?: string };
      console.log('[EmailOnboarding] OTP verify response data:', data);

      if (!response.ok) {
        console.error('[EmailOnboarding] OTP verification failed:', data.error);

        // Check for specific OTP expired message and provide a more user-friendly response
        if (data.error && data.error.toLowerCase().includes('otp expired or not found')) {
          setError('OTP expired. Please request a new code.');
        } else {
          setError(data.error || 'Failed to verify OTP');
        }

        setSuccess(false);
        return;
      }

      console.log('[EmailOnboarding] OTP verification successful');
      console.log('[EmailOnboarding] User data:', data.user);
      console.log('[EmailOnboarding] Token:', data.token ? 'Present' : 'Missing');

      // Redirect to home page after successful verification
      window.location.href = '/check-activation';
    } catch (error) {
      console.error('[EmailOnboarding] === Error in OTP Verify Process ===');
      console.error('[EmailOnboarding] Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('[EmailOnboarding] Error message:', error instanceof Error ? error.message : error);
      console.error('[EmailOnboarding] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = () => {
    setCanResend(false);
    setCountdown(30); // Reset countdown
    console.log('[EmailOnboarding] Resending OTP...');
    handleSendOTP();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp && otp.length === 6 && !isVerifying && !isInitialSending) {
      handleVerifyOTP();
    }
  };

  useEffect(() => {
    if (!canResend) {
      const timer = setTimeout(() => setCanResend(true), 300000); // 5 minutes
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [canResend]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 2000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [success, setSuccess]);

  return (
    <div className="w-full max-w-md mx-auto font-inter-display px-4">
      <div className="flex flex-col gap-6 sm:gap-9">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <h1 className="text-[#FDFDFD] text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-medium leading-[1.2em] mb-2">
            Check your inbox
          </h1>

          <p className="text-[#888888] text-base sm:text-lg lg:text-xl font-normal leading-[1.2em]">
            We've sent you a 6 digit code. Please enter it below.
          </p>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-5">
            {/* Email Display */}
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center px-4 py-3 sm:py-3 border border-input-border rounded-xl opacity-40">
                <span className="text-[#FFFFFF80] font-normal text-sm sm:text-base leading-[1.2] truncate mr-2">
                  {email}
                </span>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <MailOpen className="w-5 h-5 text-[#908B8B]" />
                </div>
              </div>

              {/* OTP Field */}
              <div className="flex justify-between items-center px-4 py-3 sm:py-2 border border-input-border rounded-xl text-white">
                <input
                  type="text"
                  placeholder="Enter OTP here"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  maxLength={6}
                  className="bg-transparent placeholder-[rgba(255,255,255,0.5)] font-normal text-base leading-[1.2] outline-none flex-1 min-h-[44px] sm:min-h-auto"
                  disabled={isLoading || isInitialSending}
                />

                <div className="flex items-center gap-1 flex-shrink-0">
                  <ShieldCheck className="w-6 h-6 text-[#908B8B]" />
                </div>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          <div className="space-y-3">
            {error && <StatusDisplay status="failed" message={error} title="Verification Failed" />}
            {success && (
              <StatusDisplay status="success" message="OTP sent successfully! Check your email." title="OTP Sent" />
            )}
            {isInitialSending && (
              <StatusDisplay status="loading" message="Sending OTP to your email..." title="Sending OTP" />
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={handleVerifyOTP}
              disabled={!otp || otp.length !== 6 || isVerifying || isInitialSending}
              className={`w-full rounded-xl py-3 sm:py-2.5 px-4 shadow-sm transition-colors text-black min-h-[48px] sm:min-h-auto ${
                otp && otp.length === 6 && !isVerifying && !isInitialSending
                  ? 'bg-[#F7F7F7] hover:bg-[#E5E5E5]'
                  : 'bg-[#CCCCCC] cursor-not-allowed'
              }`}
            >
              {isVerifying ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-[#CCCCCC] rounded-full animate-spin"></div>
                  <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Verifying...</span>
                </div>
              ) : (
                <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Continue</span>
              )}
            </button>

            {/* Back to Login */}
            <div className="text-center text-[rgba(255,255,255,0.5)] text-sm font-normal leading-[1.2em] flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
              <button
                type="button"
                className="text-white flex items-center justify-center sm:justify-start gap-1 cursor-pointer min-h-[44px] sm:min-h-auto"
                onClick={handleBackToLogin}
              >
                <ChevronLeft className="w-5 h-5 text-[#888888]" />
                <span>Back to login</span>
              </button>

              <div
                className={`text-[#888888] text-center sm:text-right min-h-[44px] sm:min-h-auto flex items-center justify-center sm:justify-end ${
                  canResend ? 'cursor-pointer' : 'opacity-50'
                }`}
                onClick={canResend ? handleResendOTP : undefined}
              >
                Didn't receive code? {canResend ? 'Resend' : `Resend (${countdown}s)`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
